import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import { JobBoardFiltersInput } from '@pflegia/data-access';
import { ReactComponent as BriefcaseIcon } from '@pflegia/ui/icons/briefcase.svg';
import { ReactComponent as BuildingIcon } from '@pflegia/ui/icons/building.svg';
import { ReactComponent as StarIcon } from '@pflegia/ui/icons/soft-star.svg';

import { GET_JOB_POSITIONS_SALARY_ESTIMATIONS } from '@shared/graphql/operations/job-positions-salary-estimations';
import { GET_LLP_MARKET_STATS } from '@shared/graphql/operations/llp-market-stats';

const NEW_JOBS_DISPLAY_FLOOR = 7;

type MarketStatsStripProps = Partial<Pick<JobBoardFiltersInput, 'jobTitleIds' | 'locations'>> & {
  totalJobs?: number;
};

const de = (value: number) => value.toLocaleString('de-DE');

const Tile: React.FC<{ icon: React.ReactNode; value: React.ReactNode; label: string; badge?: string }> = ({
  icon,
  value,
  label,
  badge,
}) => (
  <div className='relative flex flex-col rounded-3xl bg-white p-4 shadow-sm md:flex-1'>
    {badge && (
      <span className='absolute top-4 right-4 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'>{badge}</span>
    )}
    <span className='bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full'>{icon}</span>
    <span className='mt-4 text-2xl font-bold md:text-3xl'>{value}</span>
    <span className='text-text-secondary mt-1 text-sm'>{label}</span>
  </div>
);

export const MarketStatsStrip: React.FC<MarketStatsStripProps> = ({ jobTitleIds, locations, totalJobs }) => {
  const { t } = useTranslation('jobListings');
  const skip = jobTitleIds?.length !== 1 || locations?.length !== 1 || !locations[0]?.city;

  const { data: statsData } = useQuery(GET_LLP_MARKET_STATS, { skip, variables: { filters: { jobTitleIds, locations } } });
  const { data: salaryData } = useQuery(GET_JOB_POSITIONS_SALARY_ESTIMATIONS, { skip, variables: { filters: { jobTitleIds, locations } } });

  const stats = statsData?.data;
  const salary = salaryData?.data;
  if (skip || !stats || totalJobs === undefined) return null;

  const newJobs = stats.newJobsLast14Days < NEW_JOBS_DISPLAY_FLOOR ? `${NEW_JOBS_DISPLAY_FLOOR}+` : de(stats.newJobsLast14Days);
  const formattedSalary = salary?.averageSalary ? `${de(salary.averageSalary)} €` : null;

  return (
    <section className='my-6'>
      <div className='grid grid-cols-2 gap-4 md:flex'>
        <Tile icon={<BriefcaseIcon className='h-5 w-5' />} value={de(totalJobs)} label={t('marketStats.jobs')} />
        {Boolean(formattedSalary) && (
          <Tile
            icon={<span className='text-lg font-semibold'>€</span>}
            value={
              <span>
                Ø <span className='whitespace-nowrap'>{formattedSalary}</span>
              </span>
            }
            label={t('marketStats.averageSalary')}
          />
        )}
        <Tile icon={<BuildingIcon className='h-5 w-5' />} value={de(stats.uniqueEmployers)} label={t('marketStats.employers')} />
        <Tile
          icon={<StarIcon viewBox='0 0 59 58' className='[&_path]:fill-primary h-5 w-5' />}
          value={newJobs}
          label={t('marketStats.newJobs')}
          badge={t('marketStats.newBadge')}
        />
      </div>
    </section>
  );
};
